// Cave84 — Consumed Bottles Archive (Phase 1 Week 6)
// Spec §4.10 F10 + §6.3 Screen 7.
// Bottles are NEVER deleted — consumed bottles appear here in full.
// Default sort: most recent consumption first (status_changed_date desc).

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WineStyle } from '@cave84/shared-types';
import { useConsumedInventory, type InventoryRow } from '@/hooks/useInventory';
import { useCreateWishlistEntry } from '@/hooks/useWishlist';
import { useAuthStore } from '@/stores/auth.store';
import { foldSearch } from '@/utils/searchText';

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const STYLE_DOT: Record<WineStyle, string> = {
  red: 'bg-claret', white: 'bg-amber-300', rose: 'bg-pink-400',
  sparkling: 'bg-sky-300', fortified: 'bg-orange-500',
  sweet: 'bg-yellow-300', orange: 'bg-orange-400',
};

const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red', white: 'White', rose: 'Rosé', sparkling: 'Sparkling',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(ts: unknown): string {
  if (!ts) return '—';
  try {
    const d = typeof ts === 'object' && ts !== null && 'toDate' in ts
      ? (ts as { toDate: () => Date }).toDate()
      : new Date(ts as string);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function getYear(ts: unknown): number | null {
  if (!ts) return null;
  try {
    const d = typeof ts === 'object' && ts !== null && 'toDate' in ts
      ? (ts as { toDate: () => Date }).toDate()
      : new Date(ts as string);
    return d.getFullYear();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Stats strip
// ---------------------------------------------------------------------------

interface Stats {
  total: number;
  ytd: number;
  uniqueWines: number;
  topProducer: string;
  totalSpent: number;
}

function computeStats(rows: InventoryRow[]): Stats {
  const currentYear = new Date().getFullYear();
  const ytd = rows.filter(r => {
    const y = getYear(r.bottle.status_changed_date);
    return y === currentYear;
  }).length;

  const producerCounts = new Map<string, number>();
  for (const r of rows) {
    const name = r.producer.name;
    producerCounts.set(name, (producerCounts.get(name) ?? 0) + 1);
  }
  let topProducer = '—';
  let max = 0;
  for (const [name, count] of producerCounts) {
    if (count > max) { max = count; topProducer = name; }
  }

  const uniqueWines = new Set(rows.map(r => r.wine.id)).size;
  const totalSpent = rows.reduce((sum, r) => sum + (r.bottle.cost_eur_at_purchase ?? 0), 0);

  return { total: rows.length, ytd, uniqueWines, topProducer, totalSpent };
}

function StatsStrip({ stats }: { stats: Stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border-subtle rounded-lg overflow-hidden mb-6">
      {[
        { label: 'Total Consumed', value: stats.total.toString() },
        { label: 'This Year', value: stats.ytd.toString() },
        { label: 'Unique Wines', value: stats.uniqueWines.toString() },
        { label: 'Top Producer', value: stats.topProducer, small: true },
      ].map(({ label, value, small }) => (
        <div key={label} className="bg-surface-1 px-4 py-4 text-center">
          <p className={`mono font-medium text-text-primary tabular-nums ${small ? 'text-base leading-tight' : 'text-2xl'}`}>
            {value}
          </p>
          <p className="smallcaps text-text-muted mt-1" style={{ fontSize: 11 }}>{label}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// "Want again?" wishlist button (F9.3)
// ---------------------------------------------------------------------------

function WantAgainButton({ wineId }: { wineId: string }) {
  const isOwner = useAuthStore(s => s.userDoc?.role === 'owner');
  const createMutation = useCreateWishlistEntry();
  const [done, setDone] = useState(false);

  if (!isOwner || done) {
    return done ? (
      <span className="text-xs text-gold flex-shrink-0">Added</span>
    ) : null;
  }

  return (
    <button
      type="button"
      onClick={async e => {
        e.preventDefault();
        e.stopPropagation();
        await createMutation.mutateAsync({ wine_id: wineId, priority: 'medium' });
        setDone(true);
      }}
      disabled={createMutation.isPending}
      className="text-xs text-text-muted hover:text-gold border border-border-subtle hover:border-gold/40 px-2 py-0.5 rounded transition-colors flex-shrink-0 disabled:opacity-40"
    >
      Want again
    </button>
  );
}

function ConsumedRow({ row }: { row: InventoryRow }) {
  const { bottle, vintage, wine, producer } = row;
  return (
    <Link
      to={`/inventory/${bottle.id}`}
      className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors border-b border-border-subtle last:border-0 group"
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STYLE_DOT[wine.style]}`} />

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-display font-medium text-text-primary text-base truncate">{wine.cuvee_name ?? '—'}</span>
          <span className="text-sm text-text-muted flex-shrink-0">{vintage.year}</span>
        </div>
        <p className="text-sm text-text-secondary truncate">{producer.name}</p>
      </div>

      <div className="text-right flex-shrink-0">
        <p className="text-sm text-text-muted">{formatDate(bottle.status_changed_date)}</p>
        {bottle.is_special_occasion && (
          <p className="text-sm text-gold">🌟 Special</p>
        )}
      </div>

      <WantAgainButton wineId={wine.id} />
      <svg className="w-4 h-4 text-text-muted group-hover:text-text-secondary flex-shrink-0"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type SortKey = 'date_desc' | 'date_asc' | 'vintage_desc' | 'producer';

export default function ConsumedPage() {
  const { rows, isLoading } = useConsumedInventory();
  const [search, setSearch] = useState('');
  const [styleFilter, setStyleFilter] = useState<WineStyle | null>(null);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date_desc');

  const currentYear = new Date().getFullYear();

  const filtered = useMemo(() => {
    let result = [...rows];

    // Style
    if (styleFilter) result = result.filter(r => r.wine.style === styleFilter);

    // Date range (consumption year)
    if (yearFrom) {
      const from = parseInt(yearFrom);
      result = result.filter(r => {
        const y = getYear(r.bottle.status_changed_date);
        return y !== null && y >= from;
      });
    }
    if (yearTo) {
      const to = parseInt(yearTo);
      result = result.filter(r => {
        const y = getYear(r.bottle.status_changed_date);
        return y !== null && y <= to;
      });
    }

    // Search
    if (search.trim()) {
      const q = foldSearch(search.trim());
      result = result.filter(r => {
        const hay = foldSearch([
          r.producer.name,
          r.wine.cuvee_name ?? '',
          String(r.vintage.year),
          r.bottle.notes ?? '',
        ].join(' '));
        return hay.includes(q);
      });
    }

    // Sort
    result.sort((a, b) => {
      switch (sortKey) {
        case 'date_desc': {
          const da = getYear(a.bottle.status_changed_date) ?? 0;
          const db_ = getYear(b.bottle.status_changed_date) ?? 0;
          return db_ - da;
        }
        case 'date_asc': {
          const da = getYear(a.bottle.status_changed_date) ?? 0;
          const db_ = getYear(b.bottle.status_changed_date) ?? 0;
          return da - db_;
        }
        case 'vintage_desc':
          return b.vintage.year - a.vintage.year;
        case 'producer':
          return a.producer.name.localeCompare(b.producer.name);
        default:
          return 0;
      }
    });

    return result;
  }, [rows, styleFilter, yearFrom, yearTo, search, sortKey]);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const ALL_STYLES: WineStyle[] = ['red', 'white', 'rose', 'sparkling', 'fortified', 'sweet', 'orange'];

  return (
    <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="smallcaps text-text-tertiary mb-1">Archive</p>
          <h1 className="font-display text-3xl font-light text-text-primary tracking-tight">
            Consumed
          </h1>
        </div>
        {!isLoading && (
          <p className="mono text-text-tertiary tabular-nums">
            {filtered.length.toLocaleString()}
            {filtered.length !== rows.length && (
              <span className="text-text-muted"> / {rows.length.toLocaleString()}</span>
            )}
            <span className="text-text-muted ml-1 smallcaps" style={{ fontSize: 11 }}>bottles</span>
          </p>
        )}
      </div>

      {/* Stats */}
      {!isLoading && <StatsStrip stats={stats} />}

      {/* Filters */}
      <div className="bg-surface-1 border border-border-medium rounded-lg p-5 mb-6 space-y-4">
        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search producer, wine, year…"
          className="w-full px-4 py-2.5 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-base"
        />

        <div className="flex flex-wrap gap-2 items-center">
          {/* Style filter pills */}
          <button type="button" onClick={() => setStyleFilter(null)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              !styleFilter
                ? 'border-border-strong bg-surface-3 text-text-primary'
                : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'
            }`}>
            All
          </button>
          {ALL_STYLES.map(s => (
            <button key={s} type="button" onClick={() => setStyleFilter(styleFilter === s ? null : s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                styleFilter === s
                  ? 'border-border-strong bg-surface-3 text-text-primary'
                  : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'
              }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${STYLE_DOT[s]}`} />
              {STYLE_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Consumption year range */}
          <input
            type="number"
            value={yearFrom}
            onChange={e => setYearFrom(e.target.value)}
            placeholder={`From ${currentYear - 5}`}
            min={2000}
            max={currentYear}
            className="w-32 px-3 py-2 rounded border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold"
          />
          <span className="text-text-muted text-sm">–</span>
          <input
            type="number"
            value={yearTo}
            onChange={e => setYearTo(e.target.value)}
            placeholder={String(currentYear)}
            min={2000}
            max={currentYear}
            className="w-32 px-3 py-2 rounded border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold"
          />

          <div className="flex-1" />

          {/* Sort */}
          <select
            value={sortKey}
            onChange={e => setSortKey(e.target.value as SortKey)}
            className="px-3 py-2 rounded border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold"
          >
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
            <option value="vintage_desc">Vintage ↓</option>
            <option value="producer">Producer A–Z</option>
          </select>
        </div>
      </div>

      {/* Result count */}
      <p className="text-sm text-text-muted mb-3">
        {filtered.length} bottle{filtered.length !== 1 ? 's' : ''}
        {filtered.length !== rows.length && ` (of ${rows.length} total)`}
      </p>

      {/* List */}
      {isLoading ? (
        <div className="py-16 text-center text-text-muted text-sm animate-pulse">Loading archive…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-text-muted text-sm">No consumed bottles match these filters</p>
          <button type="button" onClick={() => { setSearch(''); setStyleFilter(null); setYearFrom(''); setYearTo(''); }}
            className="mt-3 text-gold text-sm hover:underline">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-border-medium overflow-hidden bg-surface-1">
          {filtered.map(row => <ConsumedRow key={row.bottle.id} row={row} />)}
        </div>
      )}
    </div>
  );
}
